import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Eye, ExternalLink, FileText, Loader2, Search, Upload } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { UploadDialog } from "@/pages/project/ProjectDocuments";
import { fmtDate, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const ALL = "ALL";
const sizeText = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const PREVIEWABLE = ["application/pdf", "image/jpeg", "image/png"];

function expiryState(d) {
  if (!d) return null;
  const days = Math.ceil((new Date(d + "T00:00:00") - new Date(todayISO() + "T00:00:00")) / 86400000);
  if (days < 0) return { tone: "bg-rose-500/10 text-rose-600 dark:text-rose-400", text: `Expired ${-days}d ago` };
  if (days <= 30) return { tone: "bg-amber-500/10 text-amber-700 dark:text-amber-400", text: `Expires in ${days}d` };
  return { tone: "bg-muted text-muted-foreground", text: `Valid to ${fmtDate(d)}` };
}

export default function AllProjectDocuments() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState(null);
  const [project, setProject] = useState(params.get("project") || ALL);
  const [folder, setFolder] = useState(ALL);
  const [expiry, setExpiry] = useState(ALL);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [pick, setPick] = useState(false);
  const [pickedProject, setPickedProject] = useState("");
  const [upload, setUpload] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (project !== ALL) params.project_id = project;
      if (folder !== ALL) params.folder = folder;
      if (expiry !== ALL) params.expiry = expiry;
      if (q) params.q = q;
      const { data } = await api.get("/project-documents", { params });
      setState(data);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setLoading(false); }
  }, [project, folder, expiry, q]);
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);

  const blob = async (d, inline) => (await api.get(`/projects/${d.project_id}/documents/${d.id}/file`, { params: inline ? { inline: true } : {}, responseType: "blob" })).data;
  const download = async (d) => { try { const url = URL.createObjectURL(await blob(d, false)); const a = document.createElement("a"); a.href = url; a.download = d.filename; a.click(); URL.revokeObjectURL(url); } catch (err) { toast.error(getErrorMessage(err)); } };
  const preview = async (d) => { try { window.open(URL.createObjectURL(await blob(d, true)), "_blank", "noopener"); } catch (err) { toast.error(getErrorMessage(err)); } };

  const writable = (state?.projects || []).filter((p) => state?.writable_project_ids?.includes(p.id));
  const stats = [["Documents", state?.total ?? 0, ""], ["Expiring in 30 days", state?.expiring_soon ?? 0, state?.expiring_soon ? "text-amber-600 dark:text-amber-400" : ""], ["Expired", state?.expired ?? 0, state?.expired ? "text-rose-600 dark:text-rose-400" : ""]];

  return (
    <div className="space-y-5" data-testid="all-project-documents-page">
      <PageHeader
        title="Project Documents"
        description="Every document uploaded to your projects, in one place. Open a project's Documents page to organise its files."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(-1)} data-testid="documents-back-button"><ArrowLeft className="h-4 w-4" /> Back</Button>
            {writable.length > 0 && <Button onClick={() => { setPickedProject(project !== ALL && writable.some((p) => p.id === project) ? project : ""); setPick(true); }} data-testid="all-documents-upload-button"><Upload className="h-4 w-4" /> Upload</Button>}
          </div>
        }
      />

      <div className="grid grid-cols-3 gap-3">
        {stats.map(([k, v, tone]) => (
          <button type="button" key={k} onClick={() => setExpiry(k === "Expired" ? "expired" : k.startsWith("Expiring") ? "soon" : ALL)} className="rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-muted/40">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{k}</p>
            <p className={`mt-1 text-2xl font-semibold tabular-nums ${tone}`}>{v}</p>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, number, project…" className="pl-8" data-testid="all-documents-search" />
        </div>
        <Select value={project} onValueChange={setProject}>
          <SelectTrigger className="w-56" data-testid="all-documents-project-filter"><SelectValue placeholder="All projects" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All projects</SelectItem>{(state?.projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name} ({p.count})</SelectItem>)}</SelectContent>
        </Select>
        <Select value={folder} onValueChange={setFolder}>
          <SelectTrigger className="w-52"><SelectValue placeholder="All folders" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>All folders</SelectItem>{(state?.folders || []).map((f) => <SelectItem key={f.name} value={f.name}>{f.name} ({f.count})</SelectItem>)}</SelectContent>
        </Select>
        <Select value={expiry} onValueChange={setExpiry}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Any expiry" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Any expiry</SelectItem><SelectItem value="soon">Expiring in 30 days</SelectItem><SelectItem value="expired">Expired</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border border-border bg-card">
        {loading && !state ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : !state?.items.length ? (
          <div className="p-6"><EmptyState title="No documents found" description={state?.total ? "Nothing matches these filters." : "No documents have been uploaded to your projects yet."} testId="all-documents-empty" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Document</TableHead><TableHead>Project</TableHead><TableHead>Expiry</TableHead><TableHead>Uploaded</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {state.items.map((d) => {
                  const ex = expiryState(d.expiry_date);
                  return (
                    <TableRow key={d.id} data-testid={`all-document-row-${d.id}`}>
                      <TableCell className="max-w-[24rem]">
                        <div className="flex items-start gap-2">
                          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5"><span className="truncate font-medium text-foreground">{d.title}</span>{d.version_count > 1 && <span className="rounded bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground">v{d.version}</span>}</div>
                            <div className="truncate text-xs text-muted-foreground">{d.folder}{d.doc_number ? ` · ${d.doc_number}` : ""}</div>
                            <div className="truncate text-[11px] text-muted-foreground/80">{d.filename} · {sizeText(d.size_bytes)}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs"><Link to={`/projects/${d.project_id}/documents`} className="font-mono font-semibold text-primary hover:underline">{d.project_code}</Link><div className="max-w-[12rem] truncate text-muted-foreground">{d.project_name}</div></TableCell>
                      <TableCell>{ex ? <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium ${ex.tone}`}>{ex.text}</span> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="whitespace-nowrap text-xs"><div>{d.uploaded_by_name || "—"}</div><div className="text-muted-foreground">{fmtDate(d.created_at)}</div></TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-0.5">
                          {PREVIEWABLE.includes(d.content_type) && <Button size="icon" variant="ghost" title="Preview" onClick={() => preview(d)}><Eye className="h-4 w-4" /></Button>}
                          <Button size="icon" variant="ghost" title="Download" onClick={() => download(d)}><Download className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" title="Open in project (versions, edit, delete)" onClick={() => navigate(`/projects/${d.project_id}/documents`)}><ExternalLink className="h-4 w-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={pick} onOpenChange={setPick}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Upload to which project?</DialogTitle><DialogDescription>Documents always belong to one project.</DialogDescription></DialogHeader>
          <div><Label>Project</Label>
            <Select value={pickedProject} onValueChange={setPickedProject}>
              <SelectTrigger data-testid="upload-project-select"><SelectValue placeholder="Choose a project" /></SelectTrigger>
              <SelectContent>{writable.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPick(false)}>Cancel</Button>
            <Button disabled={!pickedProject} onClick={() => { const input = document.createElement("input"); input.type = "file"; input.multiple = true; input.onchange = () => { if (input.files?.length) { setUpload({ projectId: pickedProject, files: Array.from(input.files) }); setPick(false); } }; input.click(); }} data-testid="upload-project-continue">Choose files</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {upload && <UploadDialog projectId={upload.projectId} init={{ files: upload.files, replaces: null }} folders={(state?.folders || []).map((f) => f.name)} defaultFolder="" state={state} onClose={() => setUpload(null)} onDone={() => { setUpload(null); load(); }} />}
    </div>
  );
}
